import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { rpc } from '../api'
import { can } from '../rbac'
import { session } from '../session'

const PROFILE_API = 'church_erp.church_erp.utils.members_api'
const PRAYER_API = 'church_erp.church_erp.utils.prayer_api'
const MASS_API = 'church_erp.church_erp.utils.mass_intentions_api'
const EXPENSES_API = 'church_erp.church_erp.utils.expenses_api'

function countBadge(key, method, params, enabled) {
  const query = useQuery({
    queryKey: computed(() => ['nav-badge', key, session.boot?.company]),
    queryFn: async () => {
      const res = await rpc(method, params())
      return Number(
        res?.total ?? res?.count ?? res?.requests?.length ?? res?.intentions?.length ?? 0,
      )
    },
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 0,
  })
  return computed(() => query.data.value || 0)
}

/** Actionable counts shown as sidebar badges. */
export function useNavBadges() {
  const authenticated = computed(() => !!session.boot?.authenticated)
  const company = () => session.boot?.company

  const profileRequests = countBadge(
    'profile-requests',
    `${PROFILE_API}.list_profile_update_requests`,
    () => ({ company: company(), status: 'Pending Approval', start: 0, page_length: 1 }),
    computed(() => authenticated.value && can('members.approve_profile')),
  )

  const prayerRequests = countBadge(
    'prayer-requests',
    `${PRAYER_API}.list_prayer_requests`,
    () => ({ company: company(), status: 'Open' }),
    computed(() => authenticated.value && can('prayer.view')),
  )

  const intentionsAwaiting = countBadge(
    'mass-intentions',
    `${MASS_API}.list_mass_intentions`,
    () => ({ company: company(), status: 'Requested', start: 0, page_length: 1 }),
    computed(() => authenticated.value && can('mass_intentions.view')),
  )

  const unpostedOutflows = countBadge(
    'unposted-outflows',
    `${EXPENSES_API}.get_unposted_outflows`,
    () => ({ company: company() }),
    computed(() => authenticated.value && can('expenses.view')),
  )

  const badges = computed(() => ({
    '/admin/profile-requests': profileRequests.value,
    '/prayer-requests': prayerRequests.value,
    '/mass-calendar': intentionsAwaiting.value,
    '/expenses': unpostedOutflows.value,
  }))

  return { badges }
}
